import type { AutomationRule } from '../api/automation';

export interface AutomationCoverageFact {
  key: string;
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'info' | 'success' | 'warning';
}

export interface AutomationCoverageAction {
  key: 'open-automation' | 'review-active-rules';
  label: 'Open Automation' | 'Review active rules';
}

export interface AutomationCoverageSummary {
  state: 'loading' | 'empty' | 'ready' | 'error';
  tone: 'info' | 'success' | 'warning';
  title: string;
  message: string;
  facts: AutomationCoverageFact[];
  action: AutomationCoverageAction | null;
}

function formatLatestRecordedProof(value: string | null) {
  if (!value) {
    return 'Not available yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Not available yet';
  }

  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getLatestAutomationProofAt(rules: AutomationRule[]) {
  return rules.reduce<string | null>((latest, rule) => {
    if (!rule.last_triggered_at) {
      return latest;
    }

    if (!latest) {
      return rule.last_triggered_at;
    }

    return new Date(rule.last_triggered_at).getTime() > new Date(latest).getTime()
      ? rule.last_triggered_at
      : latest;
  }, null);
}

export function buildAutomationCoverageSummary({
  rules,
  isLoading,
  hasError,
}: {
  rules: AutomationRule[] | null;
  isLoading: boolean;
  hasError: boolean;
}): AutomationCoverageSummary {
  const safeRules = Array.isArray(rules) ? rules : [];
  const savedRuleCount = safeRules.length;
  const activeMonitoringCount = safeRules.filter((rule) => rule.is_active).length;
  const latestRecordedProofAt = getLatestAutomationProofAt(safeRules);
  const latestRecordedProofLabel = formatLatestRecordedProof(latestRecordedProofAt);
  const sharedFacts: AutomationCoverageFact[] = [
    {
      key: 'saved-rules',
      label: 'Saved rules',
      value: savedRuleCount.toString(),
      mono: true,
    },
    {
      key: 'active-monitoring',
      label: 'Active monitoring',
      value: activeMonitoringCount.toString(),
      mono: true,
      tone: activeMonitoringCount > 0 ? 'success' : 'warning',
    },
    {
      key: 'latest-recorded-proof',
      label: 'Latest recorded proof',
      value: latestRecordedProofLabel,
      mono: true,
      tone: latestRecordedProofAt ? 'success' : 'warning',
    },
  ];

  if (isLoading) {
    return {
      state: 'loading',
      tone: 'info',
      title: 'Loading automation coverage',
      message: 'Checking saved advisory monitoring coverage for this portfolio.',
      facts: sharedFacts,
      action: null,
    };
  }

  if (hasError) {
    return {
      state: 'error',
      tone: 'warning',
      title: 'Automation coverage unavailable',
      message:
        'Pinecurl could not refresh saved advisory monitoring coverage on this route just now, so the mounted owner-route proof remains the truthful answer.',
      facts: sharedFacts,
      action: null,
    };
  }

  if (savedRuleCount === 0) {
    return {
      state: 'empty',
      tone: 'info',
      title: 'Automation coverage available',
      message:
        'No saved advisory monitoring rules are attached to this portfolio proof yet. Open Automation if you want Pinecurl to keep watching between visits.',
      facts: sharedFacts,
      action: {
        key: 'open-automation',
        label: 'Open Automation',
      },
    };
  }

  return {
    state: 'ready',
    tone: activeMonitoringCount > 0 ? 'success' : 'warning',
    title: activeMonitoringCount > 0 ? 'Automation coverage saved' : 'Automation coverage saved, but inactive',
    message: latestRecordedProofAt
      ? activeMonitoringCount > 0
        ? 'Saved advisory rules are still monitoring this account, and the latest recorded proof is visible here without reopening the workspace.'
        : 'Saved advisory rules still exist for this account, but none are actively monitoring right now. Review active rules before you rely on revisit coverage.'
      : activeMonitoringCount > 0
        ? 'Saved advisory coverage exists for this account, but the latest recorded proof is not available yet because no rule has recorded an outcome.'
        : 'Saved advisory coverage exists for this account, but none are actively monitoring right now and no latest recorded proof is available yet.',
    facts: sharedFacts,
    action: {
      key: 'review-active-rules',
      label: 'Review active rules',
    },
  };
}
